import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Appliance } from '@common/util-models';
import { combineLatestObj, filterNullUndefined } from '@domgen/dgx-fe-common';
import {
  controlValid,
  formValue,
  formValueChanges,
} from '@domgen/dgx-fe-dynamic-form-builder';
import { ComponentStore } from '@ngrx/component-store';
import {
  BrandsByApplianceState,
  BrandsState,
  DeviceCategory,
  DeviceFormValue,
  initialBrandState,
  LaptopDevice,
  MobileDevice,
  UI_BasketItem,
} from '@sky/util-models';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, pluck, startWith } from 'rxjs/operators';

export interface AddDeviceState {
  activeAppliance?: Appliance | null;
  activeBrands?: BrandsState;
  basketItem: UI_BasketItem;
  brands: BrandsByApplianceState;
  category?: string;
  initialFormsData?: DeviceFormValue;
}

export interface ViewModel {
  activeBrands: BrandsState | undefined;
  basketItem: UI_BasketItem;
  category: string | undefined;
  initialFormsData: DeviceFormValue | undefined;
  isComplete: boolean;
  isDeviceFormValid: boolean;
  isLT: boolean;
  isLTAllowed?: boolean;
}

@Injectable()
export class AddDeviceStateService extends ComponentStore<AddDeviceState> {
  public readonly ltFormSubject = new BehaviorSubject<boolean>(false);

  private readonly deviceFormSubject = new BehaviorSubject<
    UntypedFormGroup | undefined
  >(undefined);
  public readonly deviceForm$: Observable<UntypedFormGroup | undefined> =
    this.deviceFormSubject;

  //Selectors

  readonly deviceFormValueChanges$: Observable<DeviceFormValue> =
    this.deviceForm$.pipe(formValueChanges());

  readonly deviceFormValue$: Observable<DeviceFormValue> =
    this.deviceForm$.pipe(formValue());

  readonly isDeviceFormValid$: Observable<boolean> = this.deviceForm$.pipe(
    controlValid()
  );

  readonly initialFormsData$: Observable<DeviceFormValue | undefined> =
    this.select((state: AddDeviceState) => state.initialFormsData);

  readonly basketItem$: Observable<UI_BasketItem> = this.select(
    (state: AddDeviceState) => state.basketItem
  );

  readonly isComplete$: Observable<boolean> = this.select(
    (state: AddDeviceState) => !!state.basketItem.id
  );

  readonly category$: Observable<string | undefined> = this.select(
    (state: AddDeviceState) => state.category
  );

  readonly activeAppliance$: Observable<Appliance | undefined | null> =
    this.select((state: AddDeviceState) => state.activeAppliance);

  readonly deviceTypeValue$: Observable<string> = combineLatest([
    this.category$,
    this.deviceFormValueChanges$.pipe(pluck('deviceType'), startWith(null)),
  ]).pipe(
    map(([category, deviceType]) => {
      if (category === DeviceCategory.Laptop) {
        return LaptopDevice.Code;
      } else if (category === DeviceCategory.MobilePhone) {
        return MobileDevice.Code;
      } else {
        return deviceType || null;
      }
    }),
    filterNullUndefined()
  );

  readonly brands$: Observable<BrandsByApplianceState> = this.select(
    (state: AddDeviceState) => state.brands
  );

  readonly activeBrands$: Observable<BrandsState | undefined> = this.select(
    (state: AddDeviceState) => state.activeBrands
  );

  readonly activeBrandsUpdater = this.updater(
    (
      state: AddDeviceState,
      [brandsState, deviceType]: [BrandsByApplianceState, string | undefined]
    ) => {
      return {
        ...state,
        activeBrands:
          deviceType &&
          !!Object.keys(brandsState).length &&
          brandsState[deviceType]
            ? { ...brandsState[deviceType] }
            : initialBrandState,
      };
    }
  )(combineLatest([this.brands$, this.deviceTypeValue$]));

  readonly setBasketItem = this.updater(
    (state: AddDeviceState, basketItem: UI_BasketItem) => ({
      ...state,
      basketItem: basketItem,
    })
  );

  readonly setInitialFormsData = this.updater(
    (
      state: AddDeviceState,
      initialFormsData: DeviceFormValue | null | undefined
    ) => ({
      ...state,
      initialFormsData: initialFormsData ?? undefined,
    })
  );

  readonly setCategory = this.updater(
    (state: AddDeviceState, category: string | undefined | null) => ({
      ...state,
      category: category ?? undefined,
      activeBrands:
        category &&
        category === DeviceCategory.Laptop &&
        !!Object.keys(state.brands).length &&
        state.brands[LaptopDevice.Code]
          ? { ...state.brands[LaptopDevice.Code] }
          : initialBrandState,
    })
  );

  readonly setBrands = this.updater(
    (state: AddDeviceState, brands: BrandsByApplianceState) => ({
      ...state,
      brands: brands ?? undefined,
    })
  );

  readonly setActiveAppliance = this.updater(
    (state: AddDeviceState, activeAppliance: Appliance | undefined | null) => ({
      ...state,
      activeAppliance,
    })
  );

  //VM
  readonly vm$: Observable<ViewModel> = combineLatestObj({
    basketItem: this.basketItem$,
    category: this.category$,
    activeBrands: this.activeBrands$,
    isComplete: this.isComplete$,
    initialFormsData: this.initialFormsData$,
    isLT: this.ltFormSubject,
    isDeviceFormValid: this.isDeviceFormValid$,
    isLTAllowed: this.activeAppliance$.pipe(
      map((activeAppliance) => !!activeAppliance?.applianceCodeLt)
    ),
  });

  constructor() {
    super({
      basketItem: {} as UI_BasketItem,
      brands: {},
    });
  }

  resetDevice() {
    this.setCategory(null);
    this.setInitialFormsData(null);
    this.setActiveAppliance(null);
  }

  deviceForm(form: UntypedFormGroup): void {
    this.deviceFormSubject.next(form);
  }

  lossAndTheftFormChanges(lt: boolean): void {
    this.ltFormSubject.next(lt);
  }
}
